import React, { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box } from '@mantine/core';

type Props = Readonly<{
    anchorRef: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
}>;

export function BatchExpansionPortal({ anchorRef, children }: Props) {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const portalContentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const sentinelElement = sentinelRef.current;
        const portalContentElement = portalContentRef.current;

        let sentinelResizeObserver: ResizeObserver | undefined;
        let portalContentResizeObserver: ResizeObserver | undefined;
        const scrollTargets: EventTarget[] = [];
        let layoutFn: (() => void) | undefined;

        if (sentinelElement && portalContentElement) {
            const applyLayout = () => {
                const anchorElement = anchorRef.current;
                const sentinelNode = sentinelRef.current;
                const portalContentNode = portalContentRef.current;
                if (!anchorElement || !sentinelNode || !portalContentNode) {
                    return;
                }

                const sentinelRect = sentinelNode.getBoundingClientRect();
                const anchorRect = anchorElement.getBoundingClientRect();
                const topPx = sentinelRect.top - anchorRect.top;

                portalContentNode.style.top = `${topPx}px`;

                const portalContentHeight =
                    portalContentNode.getBoundingClientRect().height;
                sentinelNode.style.minHeight =
                    portalContentHeight > 0
                        ? `${portalContentHeight}px`
                        : '0px';
            };

            layoutFn = applyLayout;

            applyLayout();

            sentinelResizeObserver = new ResizeObserver(applyLayout);
            portalContentResizeObserver = new ResizeObserver(applyLayout);
            sentinelResizeObserver.observe(sentinelElement);
            portalContentResizeObserver.observe(portalContentElement);

            let scrollAncestor: HTMLElement | null =
                sentinelElement.parentElement;
            while (scrollAncestor) {
                scrollTargets.push(scrollAncestor);
                scrollAncestor = scrollAncestor.parentElement;
            }
            scrollTargets.push(window);

            scrollTargets.forEach(scrollTarget =>
                scrollTarget.addEventListener('scroll', applyLayout, {
                    passive: true,
                    capture: true,
                }),
            );
        }

        return () => {
            sentinelResizeObserver?.disconnect();
            portalContentResizeObserver?.disconnect();
            if (layoutFn) {
                const listener = layoutFn;
                scrollTargets.forEach(scrollTarget =>
                    scrollTarget.removeEventListener('scroll', listener, {
                        capture: true,
                    }),
                );
            }
            const sentinelNode = sentinelRef.current;
            if (sentinelNode) {
                sentinelNode.style.minHeight = '';
            }
        };
    }, [anchorRef]);

    const anchorElement = anchorRef.current;

    return (
        <>
            <Box
                ref={sentinelRef}
                className="w-full shrink-0 overflow-hidden pointer-events-none select-none"
                aria-hidden
            />
            {anchorElement
                ? ReactDOM.createPortal(
                      <Box
                          ref={portalContentRef}
                          className="absolute left-0 right-0 z-30 w-full max-w-full min-w-0"
                      >
                          {children}
                      </Box>,
                      anchorElement,
                  )
                : null}
        </>
    );
}
